import { GameScene } from '@Core/index';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import { TPoint } from '@GFTypes/Point';
import { gameInstance } from '@Game/GameComponent';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { EOrientation } from '@Managers/Device/UseWindowDimensions.types';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { ParticleContainer } from 'pixi.js';
import Emitter from './Emitter';
import { EmitterConfig } from './Emitter/EmitterConfig';

export enum EEmitterLayer {
    UNDER_WIN = 'under',
    ABOVE_WIN = 'above'
}

export type TParicleEmitterGameSpecificConfig = {
    name: string;
    layer: EEmitterLayer;
    atlas: string;
    prefix: string;
    frames: number;
    frameRate: number;
    landscapeX: number;
    landscapeY: number;
    portraitX: number;
    portraitY: number;
    burst: boolean;
    quantity: number;
};

export type TParticleEmitterConfig = EmitterConfig & TParicleEmitterGameSpecificConfig;

export default class ParticleEmitter {
    private static readonly PORTRAIT_SPEED_SCALE: number = 1440 / 1080;
    private reelsSpineScale = 1;

    private readonly scene: GameScene;

    private containersByAtlas = new Map<string, ParticleContainer>();

    private readonly emitters: Emitter[] = [];
    private readonly config: TParticleEmitterConfig[];

    constructor(scene: GameScene, config: TParticleEmitterConfig[]) {
        this.scene = scene;
        this.config = config;

        EventsCenter.on(EGameEvents.RESIZE_REELS, this.onResize, this);
    }

    private getOrCreateContainer(layer: EEmitterLayer, atlasKey: string): ParticleContainer {
        const mapKey = `${layer}:${atlasKey}`;
        if (!this.containersByAtlas.has(mapKey)) {
            const container = new ParticleContainer({ x: 0, y: 0, parent: this.scene.stage });
            container.zIndex = layer === EEmitterLayer.UNDER_WIN ? ELayeringOrder.UNDER_WIN_PARTICLES : ELayeringOrder.ABOVE_WIN_PARTICLES;
            this.containersByAtlas.set(mapKey, container);
        }
        return this.containersByAtlas.get(mapKey)!;
    }

    private createEmitter(): void {
        this.config.forEach((emitterConfig) => {
            const container = this.getOrCreateContainer(emitterConfig.layer, emitterConfig.atlas);

            const emitter = new Emitter(0, 0, container, {
                ...emitterConfig,

                ...(this.hasStartEndScale(emitterConfig)
                    ? {
                          scale: {
                              onEmit: () => (emitterConfig.scale as { start: number; end: number }).start * this.reelsSpineScale
                          }
                      }
                    : {}),

                ...(this.hasMinMaxSpeed(emitterConfig)
                    ? {
                          speed: {
                              onEmit: () => {
                                  const { min, max } = emitterConfig.speed as { min: number; max: number };

                                  return (min + Math.random() * (max - min)) * this.getVerticalMotionScale();
                              }
                          }
                      }
                    : {}),

                gravityY: emitterConfig.gravityY ? emitterConfig.gravityY * this.getVerticalMotionScale() : undefined
            });

            emitter.stop();

            this.emitters.push(emitter);
        });

        this.onResize();
    }

    private hasStartEndScale(emitterConfig: TParticleEmitterConfig): boolean {
        const scale = emitterConfig.scale as unknown;
        return !!scale && typeof scale === 'object' && 'start' in (scale as object) && 'end' in (scale as object);
    }

    private hasMinMaxSpeed(emitterConfig: TParticleEmitterConfig): boolean {
        const speed = emitterConfig.speed as unknown;
        return !!speed && typeof speed === 'object' && 'min' in speed && 'max' in speed;
    }

    private getVerticalMotionScale(): number {
        const virtualSize = this.scene.game.gameStore.get(EGameStoreKeys.GameVirtualSize);
        const orientation = this.scene.game.gameStore.get(EGameStoreKeys.CurrentOrientation);
        const actualHeight = this.scene.screen.height;

        let scale = actualHeight / virtualSize.y;

        if (orientation === EOrientation.PORTRAIT) {
            scale *= ParticleEmitter.PORTRAIT_SPEED_SCALE;
        }

        return scale;
    }

    private onResize(): void {
        this.emitters.forEach((emitter, index) => {
            const { x, y } = this.getPosition(this.config[index]);
            emitter.setPosition(x, y);
        });

        const reelParameters = gameInstance.gameStore.get(EGameStoreKeys.ReelParameters);
        this.reelsSpineScale = reelParameters?.reelsSpineScale ?? 1;
    }

    private getPosition(emitterConfig: TParticleEmitterConfig): TPoint {
        const virtualSize = this.scene.game.gameStore.get(EGameStoreKeys.GameVirtualSize);
        const orientation = this.scene.game.gameStore.get(EGameStoreKeys.CurrentOrientation);
        const actualWidth = this.scene.screen.width;
        const actualHeight = this.scene.screen.height;
        const configX = orientation === EOrientation.LANDSCAPE ? emitterConfig.landscapeX : emitterConfig.portraitX;
        const configY = orientation === EOrientation.LANDSCAPE ? emitterConfig.landscapeY : emitterConfig.portraitY;
        const x = (actualWidth / virtualSize.x) * configX;
        const y = (actualHeight / virtualSize.y) * configY;
        return { x, y };
    }

    public start(): void {
        if (this.emitters.length === 0) {
            this.createEmitter();
        }

        this.emitters.forEach((emitter, i) => {
            const config = this.config[i];
            if (config.burst) {
                emitter.explode(config.quantity, 0, 0);
            } else {
                emitter.start();
            }
        });
    }

    public stop(): void {
        this.emitters.forEach((emitter) => {
            emitter.stop();
        });
    }

    public destroy(): void {
        this.emitters.forEach((emitter) => {
            emitter.stop(true);
        });

        this.emitters.length = 0;

        this.containersByAtlas.forEach((container) => container.destroy());
        this.containersByAtlas.clear();

        EventsCenter.off(EGameEvents.RESIZE_REELS, this.onResize, this);
    }

    public isAlive(): boolean {
        return this.emitters.some((emitter) => emitter.getAliveParticleCount() > 0);
    }

    public update(deltaMS: number): void {
        this.emitters.forEach((emitter) => {
            emitter.update(deltaMS);
        });
    }
}
